import { ReactNode } from 'react';
import { Badge, Card, Group, Stack, Text, Title } from '@mantine/core';
import { useLanguage } from '../../providers/LanguageContext';
import { TopCards, TopOwnGoals, TopScorer } from './homeTypes';

function LeaderboardCard<T extends { personId: number; name: string }>({ title, emptyText, rows, renderValue }: {
  title: string;
  emptyText: string;
  rows: T[];
  renderValue: (row: T) => ReactNode;
}) {
  return (
    <div>
      <Title order={3} mb="sm">{title}</Title>
      {rows.length === 0 ? (
        <Text c="dimmed">{emptyText}</Text>
      ) : (
        <Card withBorder padding="sm" radius="sm">
          <Stack gap={6}>
            {rows.map((row, i) => (
              <Group key={row.personId} justify="space-between" wrap="nowrap">
                <Text size="sm">{i + 1}. {row.name}</Text>
                {renderValue(row)}
              </Group>
            ))}
          </Stack>
        </Card>
      )}
    </div>
  );
}

export function TopScorersCard({ scorers }: { scorers: TopScorer[] }) {
  const { t } = useLanguage();
  return (
    <LeaderboardCard
      title={t.home.topScorers}
      emptyText={t.home.noScorers}
      rows={scorers}
      renderValue={(s) => <Badge size="sm" variant="light" color="teal">{s.goals} {t.home.goals}</Badge>}
    />
  );
}

export function TopCardsCard({ cards }: { cards: TopCards[] }) {
  const { t } = useLanguage();
  return (
    <LeaderboardCard
      title={t.home.topCards}
      emptyText={t.home.noCards}
      rows={cards}
      renderValue={(c) => (
        <Group gap={4}>
          {c.yellow > 0 && <Badge size="sm" variant="light" color="yellow">{c.yellow}</Badge>}
          {c.red > 0 && <Badge size="sm" variant="light" color="red">{c.red}</Badge>}
        </Group>
      )}
    />
  );
}

export function TopOwnGoalsCard({ ownGoals }: { ownGoals: TopOwnGoals[] }) {
  const { t } = useLanguage();
  return (
    <LeaderboardCard
      title={t.home.topOwnGoals}
      emptyText={t.home.noOwnGoals}
      rows={ownGoals}
      renderValue={(o) => <Badge size="sm" variant="light" color="red">{o.ownGoals}</Badge>}
    />
  );
}
